import { api } from '../../../redux/services/api';

// Utility: build a URLSearchParams from a plain object, honouring arrays.
// Skips undefined / null / '' values.
const buildQS = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(v => {
        if (v !== undefined && v !== null && v !== '') {
          qs.append(`${key}[]`, v);
        }
      });
    } else if (typeof value === 'boolean') {
      qs.append(key, value ? '1' : '0');
    } else {
      qs.append(key, String(value));
    }
  });
  return qs.toString();
};

const authHeaders = token => ({ Authorization: `Bearer ${token}` });
const withEndpointArg = fn => (...args) => fn(args[2]);

// Statuses that stop the polling for a single batch.
export const TERMINAL_BATCH_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
]);

export const parserImportApi = api.injectEndpoints({
  endpoints: build => ({
    // GET /api/parser/import/available-count
    getAvailableCount: build.query({
      query: ({ token }) => ({
        url: 'api/parser/import/available-count',
        method: 'GET',
        headers: authHeaders(token),
      }),
      providesTags: ['AvailableCount'],
    }),

    // GET /api/parser/import/scope-preview?radius_km=..&filters..
    getScopePreview: build.query({
      query: ({ token, radius_km, filters = {} }) => {
        const qs = buildQS({
          radius_km,
          sources: filters.sources,
          price_min: filters.price_min,
          price_max: filters.price_max,
          types: filters.types,
          hide_imported: filters.hide_imported,
        });
        return {
          url: qs
            ? `api/parser/import/scope-preview?${qs}`
            : 'api/parser/import/scope-preview',
          method: 'GET',
          headers: authHeaders(token),
        };
      },
      providesTags: withEndpointArg(arg => [
        { type: 'ScopePreview', id: `${arg?.radius_km ?? 'na'}` },
        'ScopePreview',
      ]),
    }),

    // GET /api/parser/import/records
    getRecords: build.query({
      query: ({
        token,
        radius_km,
        filters = {},
        page = 1,
        per_page = 50,
        sort = 'distance',
      }) => {
        const qs = buildQS({
          radius_km,
          cities: filters.cities,
          sources: filters.sources,
          price_min: filters.price_min,
          price_max: filters.price_max,
          types: filters.types,
          hide_imported: filters.hide_imported,
          page,
          per_page,
          sort,
        });
        return {
          url: `api/parser/import/records?${qs}`,
          method: 'GET',
          headers: authHeaders(token),
        };
      },
      providesTags: withEndpointArg(arg => [
        { type: 'RecordsPreview', id: `${arg?.radius_km ?? 'na'}-${arg?.page ?? 1}` },
        'RecordsPreview',
      ]),
    }),

    // GET /api/parser/import/diff/{parserAdvertId}
    getDiff: build.query({
      query: ({ token, parserAdvertId }) => ({
        url: `api/parser/import/diff/${parserAdvertId}`,
        method: 'GET',
        headers: authHeaders(token),
      }),
      providesTags: withEndpointArg(arg => [
        { type: 'Diff', id: arg?.parserAdvertId },
      ]),
    }),

    // POST /api/parser/import/batches
    createBatch: build.mutation({
      query: ({ token, radius_km, filters, records }) => ({
        url: 'api/parser/import/batches',
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
        },
        body: { radius_km, filters, records },
      }),
      invalidatesTags: [
        'AvailableCount',
        'ImportBatchList',
        'ImportRuleList',
        'ScopePreview',
        'RecordsPreview',
      ],
    }),

    // GET /api/parser/import/batches/{batch}  (polled)
    getBatchStatus: build.query({
      query: ({ token, batchId }) => ({
        url: `api/parser/import/batches/${batchId}`,
        method: 'GET',
        headers: authHeaders(token),
      }),
      providesTags: withEndpointArg(arg => [
        { type: 'ImportBatch', id: arg?.batchId },
      ]),
    }),

    // POST /api/parser/import/batches/{batch}/cancel
    cancelBatch: build.mutation({
      query: ({ token, batchId }) => ({
        url: `api/parser/import/batches/${batchId}/cancel`,
        method: 'POST',
        headers: authHeaders(token),
      }),
      invalidatesTags: withEndpointArg(arg => [
        { type: 'ImportBatch', id: arg?.batchId },
        'ImportBatchList',
      ]),
    }),

    // POST /api/parser/import/batches/{batch}/retry-failed
    retryFailed: build.mutation({
      query: ({ token, batchId }) => ({
        url: `api/parser/import/batches/${batchId}/retry-failed`,
        method: 'POST',
        headers: authHeaders(token),
      }),
      invalidatesTags: withEndpointArg(arg => [
        { type: 'ImportBatch', id: arg?.batchId },
        'ImportBatchList',
        'AvailableCount',
      ]),
    }),

    // GET /api/parser/import/batches
    listBatches: build.query({
      query: ({ token, page = 1, per_page = 25 }) => {
        const qs = buildQS({ page, per_page });
        return {
          url: `api/parser/import/batches?${qs}`,
          method: 'GET',
          headers: authHeaders(token),
        };
      },
      providesTags: ['ImportBatchList'],
    }),

    listImportRules: build.query({
      query: ({ token, page = 1, per_page = 15 }) => {
        const qs = buildQS({ page, per_page });
        return {
          url: `api/parser/import/rules?${qs}`,
          method: 'GET',
          headers: authHeaders(token),
        };
      },
      providesTags: result => [
        'ImportRuleList',
        ...((result?.data || []).map(rule => ({ type: 'ImportRule', id: rule.id }))),
      ],
    }),

    getImportRule: build.query({
      query: ({ token, ruleId }) => ({
        url: `api/parser/import/rules/${ruleId}`,
        method: 'GET',
        headers: authHeaders(token),
      }),
      providesTags: withEndpointArg(arg => [{ type: 'ImportRule', id: arg?.ruleId }]),
    }),

    createImportRule: build.mutation({
      query: ({ token, body }) => ({
        url: 'api/parser/import/rules',
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: ['ImportRuleList'],
    }),

    updateImportRule: build.mutation({
      query: ({ token, ruleId, body }) => ({
        url: `api/parser/import/rules/${ruleId}`,
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: withEndpointArg(arg => ['ImportRuleList', { type: 'ImportRule', id: arg?.ruleId }]),
    }),

    deleteImportRule: build.mutation({
      query: ({ token, ruleId }) => ({
        url: `api/parser/import/rules/${ruleId}`,
        method: 'DELETE',
        headers: authHeaders(token),
      }),
      invalidatesTags: ['ImportRuleList'],
    }),

    toggleImportRule: build.mutation({
      query: ({ token, ruleId, enabled }) => ({
        url: `api/parser/import/rules/${ruleId}/toggle`,
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: { enabled },
      }),
      invalidatesTags: withEndpointArg(arg => ['ImportRuleList', { type: 'ImportRule', id: arg?.ruleId }]),
    }),

    runImportRule: build.mutation({
      query: ({ token, ruleId }) => ({
        url: `api/parser/import/rules/${ruleId}/run`,
        method: 'POST',
        headers: authHeaders(token),
      }),
      invalidatesTags: withEndpointArg(arg => [
        'ImportRuleList',
        'ImportBatchList',
        { type: 'ImportRule', id: arg?.ruleId },
        { type: 'RuleHistory', id: arg?.ruleId },
      ]),
    }),

    getImportRulePreviewCount: build.query({
      query: ({ token, ruleId }) => ({
        url: `api/parser/import/rules/${ruleId}/preview-count`,
        method: 'GET',
        headers: authHeaders(token),
      }),
      providesTags: withEndpointArg(arg => [{ type: 'ImportRule', id: arg?.ruleId }]),
    }),

    getImportRulePreviewRecords: build.query({
      query: ({ token, ruleId, page = 1, per_page = 50, sort = 'distance' }) => {
        const qs = buildQS({ page, per_page, sort });
        return {
          url: `api/parser/import/rules/${ruleId}/preview-records?${qs}`,
          method: 'GET',
          headers: authHeaders(token),
        };
      },
      providesTags: withEndpointArg(arg => [
        { type: 'RulePreviewRecords', id: arg?.ruleId },
      ]),
    }),

    getImportRuleHistory: build.query({
      query: ({ token, ruleId, limit = 20 }) => {
        const qs = buildQS({ limit });
        return {
          url: `api/parser/import/rules/${ruleId}/history?${qs}`,
          method: 'GET',
          headers: authHeaders(token),
        };
      },
      providesTags: withEndpointArg(arg => [
        { type: 'RuleHistory', id: arg?.ruleId },
      ]),
    }),

    // GET /api/parser/import/batches/{batch}/audit
    getBatchAudit: build.query({
      query: ({ token, batchId }) => ({
        url: `api/parser/import/batches/${batchId}/audit`,
        method: 'GET',
        headers: authHeaders(token),
      }),
      providesTags: withEndpointArg(arg => [
        { type: 'BatchAudit', id: arg?.batchId },
      ]),
    }),
  }),
});

export const {
  useGetAvailableCountQuery,
  useGetScopePreviewQuery,
  useGetRecordsQuery,
  useGetDiffQuery,
  useCreateBatchMutation,
  useGetBatchStatusQuery,
  useCancelBatchMutation,
  useRetryFailedMutation,
  useListBatchesQuery,
  useListImportRulesQuery,
  useGetImportRuleQuery,
  useCreateImportRuleMutation,
  useUpdateImportRuleMutation,
  useDeleteImportRuleMutation,
  useToggleImportRuleMutation,
  useRunImportRuleMutation,
  useGetImportRulePreviewCountQuery,
  useGetImportRulePreviewRecordsQuery,
  useGetImportRuleHistoryQuery,
  useGetBatchAuditQuery,
} = parserImportApi;
